import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import Layout from "../components/Layout";
import { UserAvatar } from "../components/ui/user-avatar";
import { Button } from "../components/ui/button";
import { ChipGroup } from "../components/ui/chip-group";
import { FilterDropdown } from "../components/ui/filter-dropdown";
import { SectionLabel } from "../components/ui/section-label";
import { UserDialog } from "../components/gebruikers/UserDialog";
import { User } from "../api/api";
import { useUsers, usePartners, useBranches, usePendingBranchAccessRequests } from "../hooks/queries";
import { useAuth } from "../hooks/useAuth";
import { ROLE_LABELS } from "../lib/roles";
import { cn } from "@/lib/utils";

const ROLE_ORDER: Record<string, number> = { admin: 0, kam: 1, partner_admin: 2, hmp: 3 };

// Role badge colours from the design: partner roles amber, KAM green, admin blue.
const ROLE_BADGE: Record<string, string> = {
  hmp:           "bg-status-warn-bg text-status-warn-fg",
  partner_admin: "bg-status-warn-bg text-status-warn-fg",
  kam:           "bg-status-ok-bg text-status-ok-fg",
  admin:         "bg-waves-mist text-waves-ink",
};

// Labels for the toolbar role chips.
const ROLE_CHIP_LABELS: Record<string, string> = {
  admin: "Beheerder", kam: "Key account manager", partner_admin: "Partner admin", hmp: "Hiring manager",
};

const GRID = "md:grid md:grid-cols-[minmax(0,1.4fr)_200px_minmax(0,1fr)] md:gap-4";

// missingAssignmentWarning flags the two "can't do their job yet" states
// also surfaced as grouped Taken tasks (hmp_no_branch_permissions /
// kam_no_partner_permissions) — shown inline here too since this is where
// an admin/partner_admin actually fixes them.
function missingAssignmentWarning(u: User): string | null {
  if (u.role === "hmp" && u.branch_ids.length === 0) return "Geen vestigingen";
  if (u.role === "kam" && u.partner_ids.length === 0) return "Geen partners";
  return null;
}

interface Group { key: string; name: string; rows: User[] }

export default function Gebruikersbeheer() {
  const { data: users, isLoading } = useUsers();
  const { data: partners = [] } = usePartners();
  const { data: branches = [] } = useBranches(undefined, true);
  const { data: pendingRequests = [] } = usePendingBranchAccessRequests();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const canCreate = isAdmin || currentUser?.role === "partner_admin"; // partner_admin may only create hmp/partner_admin within their own partner

  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("alle");
  const [filterPartner, setFilterPartner] = useState("alle");
  // undefined = closed, null = new account.
  const [dialogUser, setDialogUser] = useState<User | null | undefined>(undefined);

  const partnerName = (id: number) => partners.find(p => p.id === id)?.name ?? `#${id}`;
  const branchName = (id: number) => branches.find(b => b.id === id)?.name ?? `Vestiging #${id}`;

  const requestsByUser = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of pendingRequests) if (r.status === "pending") m.set(r.user_id, (m.get(r.user_id) ?? 0) + 1);
    return m;
  }, [pendingRequests]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users ?? []).filter(u => {
      if (filterRole !== "alle" && u.role !== filterRole) return false;
      if (filterPartner !== "alle" && String(u.partner_id ?? "") !== filterPartner) return false;
      if (q && !u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users, filterRole, filterPartner, search]);

  // One group per partner, alphabetically, then everyone without a partner
  // (Waves' own admins and KAMs).
  const groups = useMemo(() => {
    const byKey = new Map<string, Group>();
    for (const u of filtered) {
      const key = u.partner_id != null ? String(u.partner_id) : "waves";
      if (!byKey.has(key)) byKey.set(key, { key, name: u.partner_id != null ? partnerName(u.partner_id) : "Waves", rows: [] });
      byKey.get(key)!.rows.push(u);
    }
    const list = [...byKey.values()];
    for (const g of list) g.rows.sort((a, b) => (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99) || a.name.localeCompare(b.name));
    return list.sort((a, b) => (a.key === "waves" ? 1 : b.key === "waves" ? -1 : a.name.localeCompare(b.name)));
  }, [filtered, partners]);

  function accessText(u: User): string {
    switch (u.role) {
      case "hmp": return u.branch_ids.map(branchName).join(", ") || "—";
      case "partner_admin": return "Alle vestigingen";
      case "kam": return u.partner_ids.map(partnerName).join(", ") || "—";
      case "admin": return u.can_act_as_kam && u.partner_ids.length
        ? `Volledige toegang · KAM van ${u.partner_ids.map(partnerName).join(", ")}`
        : "Volledige toegang";
      default: return "—";
    }
  }

  // Only admins see users of every role; kam and partner_admin only ever get
  // hiring managers back from the API, so role chips would be meaningless there.
  const roleChipOptions = isAdmin
    ? [{ value: "alle", label: "Alle" }, ...["admin", "kam", "partner_admin", "hmp"].map(r => ({ value: r, label: ROLE_CHIP_LABELS[r] }))]
    : null;
  const partnerOptions = [
    { value: "alle", label: "Alle partners" },
    ...partners.map(p => ({ value: String(p.id), label: p.name })),
  ];

  const userCount = (users ?? []).length;
  const subtitle = `${userCount} ${userCount === 1 ? "account" : "accounts"} · ${partners.length} ${partners.length === 1 ? "partner" : "partners"}`;

  return (
    <>
      <Layout
        title="Gebruikersbeheer"
        subtitle={subtitle}
        actions={canCreate ? <Button onClick={() => setDialogUser(null)}>+ Nieuw account</Button> : undefined}
      >
        <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
          <div>
            <Link to="/beheer" className="text-sm font-bold text-waves-ink hover:text-waves-accent hover:no-underline">← Beheer</Link>
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:w-[280px]">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-waves-muted" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Zoek naam of e-mail…"
                aria-label="Zoek gebruikers"
                className="h-10 w-full rounded-pill border border-waves-line bg-white pl-10 pr-4 text-sm text-waves-ink outline-none transition-colors placeholder:text-waves-muted focus:border-waves-zee focus:ring-2 focus:ring-waves-zee/20"
              />
            </div>
            {roleChipOptions && (
              <ChipGroup aria-label="Filter op rol" options={roleChipOptions} value={filterRole} onChange={setFilterRole} />
            )}
            {partners.length > 1 && (
              <FilterDropdown aria-label="Filter op partner" value={filterPartner} onChange={setFilterPartner} options={partnerOptions} />
            )}
            <div className="ml-auto font-mono text-[13px] text-waves-muted">{filtered.length} rijen</div>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-card border border-waves-line bg-white">
            <div className={cn("hidden px-7 py-3.5", GRID)}>
              <SectionLabel>Naam</SectionLabel>
              <SectionLabel>Rol</SectionLabel>
              <SectionLabel>Vestigingen / partners</SectionLabel>
            </div>

            {isLoading ? (
              <div className="border-t border-waves-line px-7 py-10 text-center text-sm text-waves-muted">Laden…</div>
            ) : groups.length === 0 ? (
              <div className="border-t border-waves-line px-7 py-10 text-center text-sm text-waves-muted">Geen gebruikers gevonden.</div>
            ) : groups.map(g => (
              <React.Fragment key={g.key}>
                <div className="flex items-center gap-2.5 border-t border-waves-line bg-waves-subtle px-5 py-3.5 md:px-7">
                  <span className="text-[15px] font-black">{g.name}</span>
                  <span className="font-mono text-xs text-waves-muted">{g.rows.length}</span>
                </div>
                {g.rows.map(u => {
                  const reqs = requestsByUser.get(u.id) ?? 0;
                  const warning = missingAssignmentWarning(u);
                  const access = accessText(u);
                  return (
                    <div
                      key={u.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDialogUser(u)}
                      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDialogUser(u); } }}
                      className={cn(
                        "flex cursor-pointer flex-col gap-2 border-t border-waves-line px-5 py-4 transition-colors hover:bg-waves-subtle focus-visible:bg-waves-subtle focus-visible:outline-none md:items-center md:px-7",
                        GRID,
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3.5">
                        <UserAvatar name={u.name} pictureUrl={u.profile_picture_url} size={38} className="border-0" />
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[15px] font-bold">{u.name}</span>
                            {reqs > 0 && (
                              <span className="rounded-pill bg-status-alert-bg px-[9px] py-0.5 text-xs font-bold text-status-alert-fg">
                                {reqs === 1 ? "Vraagt toegang" : `${reqs} toegangsverzoeken`}
                              </span>
                            )}
                            {warning && (
                              <span className="rounded-pill bg-status-warn-bg px-[9px] py-0.5 text-xs font-bold text-status-warn-fg">{warning}</span>
                            )}
                            {u.id === currentUser?.id && <span className="text-[13px] text-waves-muted">(jij)</span>}
                          </div>
                          <span className="truncate text-[13px] text-waves-muted">{u.email}</span>
                        </div>
                      </div>
                      <div className="pl-[52px] md:pl-0">
                        <span className={cn("whitespace-nowrap rounded-pill px-3 py-1 text-[13px] font-bold", ROLE_BADGE[u.role] ?? ROLE_BADGE.admin)}>
                          {ROLE_LABELS[u.role] ?? u.role}{u.role === "admin" && u.can_act_as_kam && " · KAM"}
                        </span>
                      </div>
                      <div className="truncate pl-[52px] text-sm text-waves-body md:pl-0" title={access}>{access}</div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Layout>

      {dialogUser !== undefined && (
        <UserDialog
          key={dialogUser?.id ?? "new"}
          user={dialogUser}
          initialTab={dialogUser && requestsByUser.get(dialogUser.id) ? "toegang" : "account"}
          onClose={() => setDialogUser(undefined)}
        />
      )}
    </>
  );
}
